/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { PAYMENT_MODES, type PaymentMode } from '@therapy/shared-validators';
import { type ChangeEvent, useCallback, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { ConsultantInvoiceCard } from '../../components/finance/consultant-invoice-card.js';
import { Button } from '../../components/ui/button.js';
import { Modal } from '../../components/ui/modal.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { QueryState } from '../../components/ui/query-state.js';
import { type ConsultantInvoice } from '../../lib/api/consultant-invoices.js';
import {
  useApproveConsultantInvoice,
  useConsultantInvoices,
  usePayConsultantInvoice,
  useRejectConsultantInvoice,
} from '../../lib/queries/consultant-invoices.js';

const INPUT =
  'focus-ring w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white';

function AdminInvoiceActions({ invoice }: { readonly invoice: ConsultantInvoice }): JSX.Element {
  const { t } = useTranslation();
  const approve = useApproveConsultantInvoice();
  const reject = useRejectConsultantInvoice();
  const pay = usePayConsultantInvoice();
  const [modal, setModal] = useState<'reject' | 'pay' | null>(null);
  const [note, setNote] = useState('');
  const [mode, setMode] = useState<PaymentMode>('upi');

  const onApprove = useCallback(() => {
    approve.mutate(invoice.id);
  }, [approve, invoice.id]);
  const openReject = useCallback(() => {
    setModal('reject');
  }, []);
  const openPay = useCallback(() => {
    setModal('pay');
  }, []);
  const close = useCallback(() => {
    setModal(null);
  }, []);
  const onModal = useCallback((open: boolean) => {
    if (!open) {
      setModal(null);
    }
  }, []);
  const onNote = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
    setNote(e.target.value);
  }, []);
  const onMode = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    setMode(e.target.value as PaymentMode);
  }, []);
  const doReject = useCallback(() => {
    reject.mutate({ id: invoice.id, note }, { onSettled: close });
  }, [reject, invoice.id, note, close]);
  const doPay = useCallback(() => {
    pay.mutate({ id: invoice.id, paymentMode: mode }, { onSettled: close });
  }, [pay, invoice.id, mode, close]);

  return (
    <>
      {invoice.status === 'submitted' && (
        <>
          <Button type="button" variant="outline" onClick={openReject}>
            {t('consultantInvoice.reject')}
          </Button>
          <Button type="button" onClick={onApprove} disabled={approve.isPending}>
            {t('consultantInvoice.approve')}
          </Button>
        </>
      )}
      {invoice.status === 'approved' && (
        <Button type="button" onClick={openPay}>
          {t('consultantInvoice.markPaid')}
        </Button>
      )}

      <Modal
        open={modal === 'reject'}
        onOpenChange={onModal}
        title={t('consultantInvoice.reject')}
        closeLabel={t('common.cancel')}
      >
        <div className="space-y-3">
          <textarea
            rows={3}
            className={INPUT}
            value={note}
            onChange={onNote}
            placeholder={t('consultantInvoice.rejectReason')}
          />
          <div className="flex justify-end">
            <Button
              type="button"
              onClick={doReject}
              disabled={note.trim() === '' || reject.isPending}
            >
              {t('consultantInvoice.reject')}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={modal === 'pay'}
        onOpenChange={onModal}
        title={t('consultantInvoice.markPaid')}
        closeLabel={t('common.cancel')}
      >
        <div className="space-y-3">
          <select className={INPUT} value={mode} onChange={onMode}>
            {PAYMENT_MODES.map((m) => (
              <option key={m} value={m}>
                {t(`settings.billing.mode.${m}`)}
              </option>
            ))}
          </select>
          <div className="flex justify-end">
            <Button type="button" onClick={doPay} disabled={pay.isPending}>
              {t('consultantInvoice.markPaid')}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}

/** Admin → Consultant Invoices (BRD v2.0 CR-006): approve / reject / pay the queue. */
export function AdminConsultantInvoicesPage(): JSX.Element {
  const { t } = useTranslation();
  const query = useConsultantInvoices();
  return (
    <div className="p-8">
      <PageHeader
        title={t('consultantInvoice.adminTitle')}
        subtitle={t('consultantInvoice.adminSubtitle')}
      />
      <QueryState
        isLoading={query.isLoading}
        isError={query.isError}
        isEmpty={(query.data ?? []).length === 0}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel={t('consultantInvoice.emptyAdmin')}
      >
        <div className="space-y-3">
          {(query.data ?? []).map((invoice) => (
            <ConsultantInvoiceCard key={invoice.id} invoice={invoice} showTherapist>
              <AdminInvoiceActions invoice={invoice} />
            </ConsultantInvoiceCard>
          ))}
        </div>
      </QueryState>
    </div>
  );
}
